import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";
import { EditRoad, Gesture, Polyline } from "@mui/icons-material";
import { Alert, AlertTitle } from "@mui/material";
import { Feature } from "geojson";

import lassoSelect from "fond/draw/modes/base/lassoSelect";
import readOnlySelect from "fond/draw/modes/base/readOnlySelect";
import MultiLayerEditPanelContent from "fond/draw/MultiLayerEdit/MultiLayerEditPanelContent";
import { MultiLayerEditLegacyProvider } from "fond/draw/MultiLayerEdit/MultiLayerEditProviderLegacy";
import { SubLayoutPopout } from "fond/layout/SubLayoutPopout";
import { MapContext } from "fond/map/MapProvider";
import { GenericToggleButton, RedoButton, ToolbarConfig, UndoButton } from "fond/map/Toolbar";
import { getCurrentProjectData } from "fond/project";
import { trafficLightData, TrafficLightValue } from "fond/project/featureProperties/PathPreferenceEdit";
import { SelectIcon } from "fond/svg_icons";
import { featureCollection } from "fond/turf";
import { LayerIds } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";

import selectSides from "./modes/selectSides";
import PathPreferencingContent from "./PathPreferencePopout";

import { InfoAlert, InfoSectionContainer } from "./PathPreferencing.styles";

const toolConfig: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [
      <GenericToggleButton
        key="inspect"
        icon={<SelectIcon />}
        modeValue="simple_select"
        title="Inspect"
        variant="full"
        subtitle="View / edit one path"
      />,
      <GenericToggleButton
        key="selectPolygon"
        icon={<Gesture />}
        modeValue="lasso_select"
        title="Lasso area"
        variant="full"
        subtitle="Draw around paths"
      />,
      <GenericToggleButton
        key="selectSide"
        icon={<Polyline />}
        modeValue="select_side"
        title="Select a side"
        variant="full"
        subtitle="Click a side"
      />,
      <GenericToggleButton
        key="selectRoad"
        icon={<EditRoad />}
        modeValue="road_select"
        title="Select a road"
        variant="full"
        subtitle="Click a road"
        disabled
      />,
    ],
  },
  {
    id: "historyTools",
    type: "action",
    items: [
      <UndoButton key="undo" variant="full" subtitle="Undo last change" />,
      <RedoButton key="redo" variant="full" subtitle="Redo last change" />,
    ],
  },
];

/**
 * This component is responsible for managing the path preferencing.
 */
const PathPreferencing: React.FC = () => {
  const { map, drawControl } = useContext(MapContext);
  const versionId = useAppSelector((state) => state.project.versionId);
  const projectData = useAppSelector((state) => getCurrentProjectData(state.project));
  const [selected, setSelected] = useState<Feature[]>([]);
  const [mode, setMode] = useState<string>("simple_select");
  const [activeTool, setActiveTool] = useState<string>("simple_select");
  const activeToolRef = useRef<string>("simple_select");

  // Configure drawing options with layer-specific styles
  const defaultOptions: MapboxDrawOptions = useMemo(
    () => ({
      modes: {
        simple_select: readOnlySelect,
        select_side: selectSides,
        road_select: readOnlySelect,
        lasso_select: lassoSelect,
      },
      editOptions: {
        allLayers: [
          {
            ID: LayerIds.inStreet,
            GeometryType: "LineString",
            StyleOverrides: [
              {
                id: "prefer-path-line",
                type: "line",
                filter: [
                  "all",
                  ["any", ["<", ["get", "user_CostFactor"], 1], ["==", ["get", "user_CostFactor"], null]],
                  ["==", ["get", "user_layerId"], "input_layers/ug_path"],
                ],
                paint: {
                  "line-color": "#00FF00",
                  "line-width": ["case", ["==", ["get", "user_Crossing"], "YES"], 2, 4],
                },
              },
              {
                id: "avoid-path-line",
                type: "line",
                filter: [
                  "all",
                  ["any", [">", ["get", "user_CostFactor"], 1], ["==", ["get", "user_CostFactor"], null]],
                  ["==", ["get", "user_layerId"], "input_layers/ug_path"],
                ],
                paint: {
                  "line-color": "#FF0000",
                  "line-width": ["case", ["==", ["get", "user_Crossing"], "YES"], 2, 4],
                },
              },
              {
                id: "neutral-path-line",
                type: "line",
                filter: [
                  "all",
                  ["any", ["==", ["get", "user_CostFactor"], 1], ["==", ["get", "user_CostFactor"], null]],
                  ["==", ["get", "user_layerId"], "input_layers/ug_path"],
                ],
                paint: {
                  "line-color": "#AAA",
                  "line-width": ["case", ["==", ["get", "user_Crossing"], "YES"], 2, 4],
                },
              },
              {
                id: "gl-draw-line-active",
                type: "line",
                filter: ["all", ["==", "$type", "LineString"], ["==", "active", "true"]],
                paint: {
                  "line-color": "rgb(33, 150, 243)",
                  "line-opacity": 0.5,
                  "line-width": 10,
                },
              },
            ],
          },
        ],
        editingLayerId: LayerIds.inStreet,
        showLayerSelection: false,
      },
    }),
    []
  );

  // Get features from Redux (same source as MapContent) to ensure IDs match what's on the map
  const initialFeatures = useMemo(() => {
    if (!projectData?.layers) return undefined;

    const allFeatures: Feature[] = [];
    const layerKeys = [LayerIds.inStreet];

    layerKeys.forEach((layerKey) => {
      const layerData = projectData.layers[layerKey];
      if (layerData?.features) {
        allFeatures.push(...layerData.features);
      }
    });

    return allFeatures.length > 0 ? featureCollection(allFeatures) : undefined;
  }, [projectData]);

  /**
   * Monitor when the selectionchange event occurs so that we can disabled
   * this feature based on the number of selected features.
   */
  const handleOnSelect = useCallback(
    ({ features }: { features: Feature[] }) => {
      setSelected(features);
      const tool = activeToolRef.current;
      if (!tool || tool === "simple_select") return;

      // Features deselected (e.g. clicked on map) — return to the active tool
      if (features.length === 0) {
        drawControl.current?.changeMode(tool);
        map?.fire("draw.modechange", { type: "draw.modechange", mode: tool, userInitiated: true });
      }
    },
    [map, drawControl]
  );

  const handleModeChange = useCallback(({ mode: newMode, userInitiated }: { mode: string; userInitiated?: boolean }) => {
    setMode(newMode);
    if (userInitiated) {
      setActiveTool(newMode);
      activeToolRef.current = newMode;
    }
  }, []);

  useEffect(() => {
    map?.on("draw.selectionchange", handleOnSelect);
    map?.on("draw.modechange", handleModeChange);

    return () => {
      map?.off("draw.selectionchange", handleOnSelect);
      map?.off("draw.modechange", handleModeChange);
    };
  }, [map, handleOnSelect, handleModeChange]);

  return (
    <MultiLayerEditLegacyProvider versionId={versionId} options={defaultOptions} config={toolConfig} initialFeatures={initialFeatures}>
      <MultiLayerEditPanelContent title="Path Preferencing">
        <Alert severity="info" data-testid="layer-alert" variant="compact" sx={{ mb: 2 }}>
          Path preferencing allow you to indicate which routes the solver should favor, avoid, or treat neutrally.
        </Alert>
        <InfoSectionContainer>
          <InfoAlert borderColor="#AAA" variant="compact" severity="info" icon={trafficLightData[TrafficLightValue.neutral].icon}>
            <AlertTitle>Neutral</AlertTitle>
            Default preference. The solver treats these paths equally with no bias for or against using them.
          </InfoAlert>
          <InfoAlert borderColor="#FF0000" variant="compact" severity="info" icon={trafficLightData[TrafficLightValue.avoid].icon}>
            <AlertTitle>Avoid</AlertTitle>
            The solver will try to avoid these paths where possible, using them only when no alternative route exists.
          </InfoAlert>
          <InfoAlert borderColor="#00CC00" variant="compact" severity="info" icon={trafficLightData[TrafficLightValue.prefer].icon}>
            <AlertTitle>Prefer</AlertTitle>
            The solver will favour these paths over neutral ones, prioritising them when planning routes.
          </InfoAlert>
        </InfoSectionContainer>
      </MultiLayerEditPanelContent>
      <SubLayoutPopout
        open={selected.length > 0 && (mode === "lasso_select" || mode === "simple_select" || mode === "select_side")}
        tabId="path-preferences-popout"
        rect={{ x: 205, y: 39, width: 300 }}
      >
        <PathPreferencingContent features={selected} returnMode={activeTool} />
      </SubLayoutPopout>
    </MultiLayerEditLegacyProvider>
  );
};

export default PathPreferencing;
